import {
  type GridRowEditStopParams,
  GridRowEditStopReasons,
  type GridRowId,
  GridRowModes,
  type GridRowModesModel,
  gridRowTreeSelector,
} from '@mui/x-data-grid-premium';
import { useEffect, useRef } from 'react';
import type { GridApiRef } from '../DataGrid';
import { isGridCellEditable, useGetNextEditableRow } from './adjacency';

/**
 * ENTER OPENS THE EDITOR IT ARRIVES IN — the Sheets/Excel rapid-entry cascade.
 *
 * Ruling R-B (Deep, 2026-08-22). Committing with Enter and landing on a DEAD
 * cell costs the user a whole keypress per row of a column:
 *
 *   BEFORE            AFTER
 *   ──────            ─────
 *   Enter  commit+move   Enter  commit+move+OPEN
 *   Enter  open          type …
 *   type …              ← one press per row, hands never leave the keys
 *
 * MUI stops the row with `cellToFocusAfter:'below'`, and that path ends at
 * `moveFocusToRelativeCell` ─▶ `setCellFocus` — focus, and nothing else
 * (useGridRowEditing.js:210-224, useGridFocus.js:169-220). The destination row
 * is left in VIEW mode with no editor mounted.
 *
 * ── WHY TWO EVENTS AND NOT A KEY BINDING ────────────────────────────────────
 *
 * The obvious fix — bind plain Enter in the document keymap next to Shift+Enter
 * — was REJECTED, for a measured reason. That keymap runs at document CAPTURE
 * and `stopPropagation()`s, so binding Enter takes it away from the editor that
 * is currently open. `MuiAutocomplete` consumes Enter to pick the highlighted
 * option (useAutocomplete.js:674-689) — the exact event k0-enter-advance spent a
 * whole PR letting through. A capture binding would re-break it for every user
 * who opens a listbox and arrows to an option without typing a filter, because
 * the blur-time hand-over (`commitPendingOnBlur`) only fires while the input
 * holds the user's own filter text.
 *
 * So this hook never touches the keydown. MUI's Enter pipeline runs in full —
 * editor pick, pending flush, commit, focus move — and the cascade is bolted on
 * behind it:
 *
 *   rowEditStop (reason: enterKeyDown)     ──▶ ARM  { rowId, field }
 *   rowModesModelChange {0:{mode:'view'}}      source still listed — WAIT
 *   cellFocusIn id=1                           MUI moves the caret
 *   rowModesModelChange {}                 ──▶ FIRE startRowEditMode(1)
 *
 * ── WHY NOT `cellFocusIn`, WHICH IS THE OBVIOUS SEAM ────────────────────────
 *
 * Because a `startRowEditMode` fired there is SILENTLY CLOBBERED, measured on
 * this branch before this shape was written. `EditableDataGrid` passes a
 * CONTROLLED `rowModesModel` (from `useSingleClickEdit`), and MUI keeps a ref of
 * the last model the PROP handed it — a ref that only catches up on the next
 * render (useGridRowEditing.js:246-256). `cellFocusIn` is published from inside
 * `moveFocusToRelativeCell`, i.e. mid-`finishRowEditMode`, and the very next
 * statement is the source row's own `updateRowInRowModesModel(id, null)`, which
 * rebuilds the model from that stale ref:
 *
 *     cellFocusIn ─▶ ours  {0:view, 1:EDIT} ──▶ setRowModesModel   ┐ same tick,
 *     finish      ─▶ MUI   {0:view} − 0 = {} ──▶ setRowModesModel  ┘ MUI wins
 *                                            └─ row 1's edit never happened
 *
 * Waiting for the model to come back WITHOUT the source row means MUI's own
 * bookkeeping is finished and its ref is current, so the cascade's write is the
 * last one in. No timers, no `queueMicrotask` — the grid tells us when it is
 * done, and a `processRowUpdate` promise simply delays that signal.
 *
 * ── THE ARM IS SINGLE-SHOT ──────────────────────────────────────────────────
 *
 * `moveFocusToRelativeCell` CLAMPS at the last row, so the end of a column moves
 * no focus at all — the arm still clears on that cycle's model change, and the
 * `destination.id === exit.rowId` guard is what turns it into a no-op.
 *
 * ── THE DESTINATION MAY BE A DEAD ROW ───────────────────────────────────────
 *
 * Enter's landing row can be locked for that column while rows below it are
 * not — `cost` / `weight delivered` are editable only on EXTERNAL rows, so a
 * couple of internal rows sandwiched between them used to END the cascade:
 *
 *   BEFORE                     AFTER
 *   ──────                     ─────
 *   row 1  external ◄ Enter    row 1  external ◄ Enter
 *   row 2  internal ── dead    row 2  internal ─┐ skipped
 *   row 3  internal            row 3  internal ─┘
 *   row 4  external            row 4  external ◄ editor opens here
 *   └─ cascade DIES at row 2   └─ seeks on, exactly like Tab
 *
 * The seek is `useGetNextEditableRow` — the same filtered+sorted sibling walk
 * Tab uses, so ONE traversal rule serves both keys. When no row below accepts
 * an editor it returns `undefined` and the cascade clamps, which is also what
 * the end of the column does.
 *
 * ── THE GUARDS ──────────────────────────────────────────────────────────────
 *
 *   same row      the column ENDED — clamped, so open nothing
 *   other field   Enter moves DOWN A COLUMN; anything else is not this cascade
 *   still listed  the source row is back in the model (a REJECTED save parks it
 *                 in Edit) — the cascade simply never fires
 *   already edit  never restart a row that is open (it would reset the caret)
 *   canEdit       a read-only grid gets no cascade — structural too (no row can
 *                 be in edit, so `rowEditStop` never fires), belt and braces
 *   per-row       `isGridCellEditable` — a column editable on row A and locked
 *                 on row B must not hand the user an editor on B; the cascade
 *                 SEEKS PAST row B rather than dying on it
 *
 * Escape and Tab are excluded by the REASON check alone: MUI classifies them as
 * `escapeKeyDown` / `tabKeyDown`, so discarding can never drag an editor down
 * the column behind it. Shift+Enter is already whole — the document keymap owns
 * it and `useGoToCell` starts row edit on arrival — so it never reaches here.
 */
export function useEnterEditCascade(apiRef: GridApiRef, opts: { canEdit?: boolean } = {}): void {
  const { canEdit = true } = opts;
  const getNextEditableRow = useGetNextEditableRow(apiRef);

  /** The Enter-driven exit in flight: null whenever no cascade is owed. */
  const armed = useRef<{ rowId: GridRowId; field: string } | null>(null);

  useEffect(() => {
    const api = apiRef.current;

    // The grid's own children mount before this effect, so `api` is live by now.
    // The guard is for a torn-down or not-yet-booted ref only, and it fails SAFE:
    // no cascade, never a throw inside a grid's mount.
    if (!api?.subscribeEvent) {
      return undefined;
    }

    const unsubscribeStop = api.subscribeEvent('rowEditStop', (params: GridRowEditStopParams) => {
      armed.current =
        params.reason === GridRowEditStopReasons.enterKeyDown && params.field
          ? { rowId: params.id, field: params.field }
          : null;
    });

    const unsubscribeModes = api.subscribeEvent('rowModesModelChange', (model: GridRowModesModel) => {
      const exit = armed.current;

      if (!exit) {
        return;
      }

      // The source row is still in the model, so MUI has not finished tearing
      // its edit down — see the WHY-NOT-cellFocusIn note above. A rejected save
      // parks the row back in Edit and this simply never fires.
      if (model[exit.rowId]) {
        return;
      }

      armed.current = null;

      if (!canEdit) {
        return;
      }

      const destination = api.state.focus.cell;

      // The column ended (clamped, so focus never moved), or focus went
      // somewhere Enter did not send it — not the destination we armed for.
      if (!destination || destination.id === exit.rowId || destination.field !== exit.field) {
        return;
      }

      // MUI's Enter exit moves focus one row DOWN. On the last data row that destination is the
      // auto-generated group footer (the TOTALS row), which has no editable cells, so the roving
      // tabindex would strand there. Put focus back on the row just committed — the same place
      // MUI's own clamp leaves it when a column ends.
      //
      // Deliberately does NOT reopen an editor here. Doing so made Enter on the last row look
      // like the row "re-opened" without saving, which is worse than the strand it replaced.
      const destinationType = gridRowTreeSelector(apiRef)[destination.id]?.type;

      if (destinationType === 'footer' || destinationType === 'pinnedRow') {
        api.setCellFocus(exit.rowId, exit.field);

        return;
      }

      // MUI moved focus DOWN EXACTLY ONE ROW. That row can be locked for this
      // column while rows below it are not, so seek on instead of ending the
      // cascade at the first dead row — see THE DESTINATION MAY BE A DEAD ROW.
      const target = isGridCellEditable(api, destination.id, destination.field)
        ? destination.id
        : getNextEditableRow(destination.id, destination.field, 'forward');

      // `undefined` is the clamp: nothing below this row takes an editor.
      if (target === undefined || api.getRowMode(target) === GridRowModes.Edit) {
        return;
      }

      api.startRowEditMode({ id: target, fieldToFocus: destination.field });

      // Only when the seek skipped past MUI's landing row: the grid's focus
      // state still points at the dead cell, and an editor open away from the
      // focused cell is a desync. Mirrors `useGoToCell`'s start-then-focus
      // order — the Tab path's proven sequence.
      if (target !== destination.id) {
        api.setCellFocus(target, destination.field);
      }
    });

    return () => {
      armed.current = null;
      unsubscribeStop();
      unsubscribeModes();
    };
  }, [apiRef, canEdit, getNextEditableRow]);
}

export default useEnterEditCascade;
